import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Flame } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const BRONZE = "#A89880";
const PERKS = [
  "On-demand training programs — pick one, press start",
  "The full high-protein recipe library",
  "Cycle syncing — train & eat with your body, not against it",
  "Members-only community Q&A",
  "Nutrition, habit & progress tracking",
  "Weekly meal planner with auto grocery lists",
];

export default function Fenyx() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);
  const [wl, setWl] = useState({ name: "", email: "", instagram: "" });
  const [wlDone, setWlDone] = useState(false);

  useEffect(() => {
    api.get("/public/config").then((r) => setLive(!!r.data.fenyx_live)).catch(() => {});
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/fenyx-waitlist", wl);
      setWlDone(true);
    } catch { setWlDone(true); }
    finally { setBusy(false); }
  };

  const checkout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "fenyx_monthly",
        origin_url: window.location.origin,
        signup: form,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-12 py-14" style={{ background: "#141311" }} data-testid="fenyx-page">
      <div className="max-w-[1000px] mx-auto">
        <Link to="/" className="lp-label-sm hover:opacity-80 transition-opacity" style={{ color: BRONZE }}>← Wellness Way with DJ</Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mt-14">
          <div>
            <p className="lp-label-sm flex items-center gap-2" style={{ color: BRONZE }}>
              <Flame size={13} /> The Membership
            </p>
            <h1 className="lp-h text-6xl md:text-7xl mt-5" style={{ color: "#F5F1EA" }}>FENYX</h1>
            <p className="lp-it text-2xl md:text-3xl mt-2" style={{ color: BRONZE }}>rise from the ashes.</p>
            <p className="mt-6 leading-relaxed max-w-md" style={{ color: "#B5AC9E" }}>
              Self-guided training, nourishment, and rhythm — the same intentional standard as
              Wellness Way coaching, in an app you carry every day.
            </p>
            <ul className="mt-8 space-y-3">
              {PERKS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px]" style={{ color: "#D8D2C7" }}>
                  <Check size={14} className="mt-1 flex-shrink-0" style={{ color: BRONZE }} /> {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-8 md:p-10" style={{ border: "1px solid rgba(168,152,128,0.35)" }}>
            {!live ? (
              wlDone ? (
                <div data-testid="fenyx-page-wl-success">
                  <p className="lp-label-sm" style={{ color: BRONZE }}>You're in</p>
                  <h3 className="lp-h text-3xl mt-4" style={{ color: "#F5F1EA" }}>Welcome to the waitlist.</h3>
                  <p className="mt-4 leading-relaxed" style={{ color: "#B5AC9E" }}>
                    Check your inbox — founding members will be the first through the door, at the
                    best price it will ever be.
                  </p>
                </div>
              ) : (
                <form onSubmit={joinWaitlist} className="space-y-4" data-testid="fenyx-page-waitlist">
                  <p className="lp-label-sm" style={{ color: BRONZE }}>Coming Soon · Join the founding waitlist</p>
                  <p className="text-sm" style={{ color: "#B5AC9E" }}>Doors open soon at $30/mo — waitlist members get first access and founding pricing.</p>
                  <input required placeholder="First name" value={wl.name}
                    onChange={(e) => setWl({ ...wl, name: e.target.value })}
                    data-testid="fenyx-page-wl-name"
                    className="w-full px-4 py-3 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
                  <input required type="email" placeholder="Email" value={wl.email}
                    onChange={(e) => setWl({ ...wl, email: e.target.value })}
                    data-testid="fenyx-page-wl-email"
                    className="w-full px-4 py-3 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
                  <input placeholder="Instagram handle (optional)" value={wl.instagram}
                    onChange={(e) => setWl({ ...wl, instagram: e.target.value })}
                    data-testid="fenyx-page-wl-instagram"
                    className="w-full px-4 py-3 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
                  <button disabled={busy} data-testid="fenyx-page-wl-submit"
                    className="w-full py-4 lp-label-sm transition-opacity hover:opacity-85 disabled:opacity-50"
                    style={{ background: BRONZE, color: "#141311" }}>
                    {busy ? "Joining…" : "Join the FENYX Waitlist"}
                  </button>
                </form>
              )
            ) : (
            <>
            <div className="flex items-end gap-2">
              <span className="lp-h text-6xl" style={{ color: "#F5F1EA" }} data-testid="fenyx-price">$30</span>
              <span className="text-sm mb-2" style={{ color: "#B5AC9E" }}>/ month · cancel anytime</span>
            </div>
            <form onSubmit={checkout} className="space-y-4 mt-8">
              <p className="lp-label-sm" style={{ color: BRONZE }}>Create your account & join</p>
              <input required placeholder="Full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="fenyx-checkout-name"
                className="w-full px-4 py-3 text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
              <input required type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="fenyx-checkout-email"
                className="w-full px-4 py-3 text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
              <input required type="password" minLength={8} placeholder="Choose a password (8+ characters)" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="fenyx-checkout-password"
                className="w-full px-4 py-3 text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
              <button disabled={busy} data-testid="fenyx-checkout-submit"
                className="w-full py-4 lp-label-sm transition-opacity hover:opacity-85 disabled:opacity-50"
                style={{ background: BRONZE, color: "#141311" }}>
                {busy ? "Redirecting to secure checkout…" : "Join FENYX — $30/mo"}
              </button>
              <p className="text-xs text-center" style={{ color: "#8A8071" }}>
                Secure checkout by Stripe. Already a member? <Link to="/login" className="underline" style={{ color: BRONZE }}>Log in</Link>
              </p>
            </form>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
